import React, { useCallback, useEffect, useState } from 'react';
import { tmRequest } from '../../utils/tmApi';
import { chipLabel, isTm, isTmPending } from '../../utils/tmPricing';
import { canDecideDefect, causeLabel, defectDecision, stamp } from '../../utils/tmDefects';
import TmDefectDecisionDialog from './TmDefectDecisionDialog.jsx';

const EMPTY = { soId: null, data: null, loading: false, error: '' };

/**
 * GET /sales_orders/{id}/tm_defects for a Time & Materials order (read-only, no PIN). A fixed-price
 * order (or none) sends NOTHING and returns {data: null}. Re-reads when the order, its pricing status
 * or `tick` changes, and on reload(); while `paused` (the pricing window is open and owns the
 * defects) it does not read, and re-reads once the window closes.
 * Returns {data, loading, error, reload}.
 */
export function useTmDefects(header, tick = 0, paused = false) {
  const soId = header ? header.sales_order_id : null;
  const tm = isTm(header);
  const status = header ? header.tm_pricing_status : null;
  const [state, setState] = useState(EMPTY);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!tm || !soId) {
      setState((s) => (s === EMPTY ? s : EMPTY));
      return undefined;
    }
    if (paused) return undefined;
    let alive = true;
    setState((s) => ({ soId, data: s.soId === soId ? s.data : null, loading: true, error: '' }));
    tmRequest('GET', `/sales_orders/${soId}/tm_defects`).then((r) => {
      if (!alive) return;
      setState((s) => ({
        soId,
        data: r.ok ? r.data : (s.soId === soId ? s.data : null),
        loading: false,
        error: r.ok ? '' : r.text,
      }));
    });
    return () => { alive = false; };
  }, [tm, soId, status, tick, paused, reloadTick]);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);
  const mine = state.soId === soId && tm;
  return { data: mine ? state.data : null, loading: mine ? state.loading : false, error: mine ? state.error : '', reload };
}

/** The billing-decision chip of one defect (T&M orders): Charged (the default) or Shop error. */
export function TmDecisionChip({ decision }) {
  if (!decision) return null;
  const title = decision.decided
    ? `${decision.label}${decision.by ? ` · ${decision.by}` : ''}${decision.at ? ` · ${stamp(decision.at)}` : ''}${decision.why ? ` · ${decision.why}` : ''}`
    : 'Charged: the default (no decision recorded)';
  return (
    <span className={`badge ms-1 ${decision.shopError ? 'bg-warning text-dark' : 'bg-secondary'}`} title={title} data-testid="tm-decision-chip">
      {decision.label}
    </span>
  );
}

function jobText(jobId, jobNums) {
  const n = jobNums && jobNums[jobId];
  return n ? `#${n}` : `id ${jobId}`;
}

/**
 * Defects of a Time & Materials order (slice 1d, Commit C) — above the production deep dive on the
 * Sales Order page. Per defect: job, part, station, pieces, the neutral cause the station picked,
 * who recorded it, the billing decision (charged by default; a shop error names who decided, when
 * and why) and the live remake links. On a PENDING order (tm_defects.editable) an office user can
 * change a decision here (the same dialog as the pricing window: Admin / Manager PIN); on a priced
 * order it is read-only. Remake links are confirmed / removed in Approve T&M Pricing → Scrap.
 *
 * Props: header (the SO header), defects ({data, loading, error, reload} from useTmDefects),
 *        jobNums ({job_id: master_job_num}, optional), onDecided (after a decision is saved).
 */
export default function TmDefectsPanel({ header, defects, jobNums, onDecided }) {
  const [deciding, setDeciding] = useState(null);
  const { data, loading, error, reload } = defects || {};
  const soId = header ? header.sales_order_id : null;
  useEffect(() => { setDeciding(null); }, [soId]);
  if (!isTm(header)) return null;
  const list = data && Array.isArray(data.defects) ? data.defects : [];
  // The payload AND the header must say pending: right after an approval the old payload
  // (editable: true) stays on screen until the re-read returns, but the header is already final.
  const editable = !!(data && data.editable) && isTmPending(header);
  const staged = data && Array.isArray(data.staged) ? data.staged : [];
  const orphans = data && data.orphans ? ((data.orphans.decisions || []).length + (data.orphans.links || []).length) : 0;
  const items = data ? [...(data.readiness || []), ...(data.warnings || [])] : [];

  return (
    <div className="card shadow-sm mb-4" data-testid="tm-defects-panel">
      <div className="card-header d-flex align-items-center gap-2">
        <i className="bi bi-exclamation-octagon text-danger"></i>
        <strong>T&amp;M defects</strong>
        {data && <span className="badge bg-light text-dark border">{list.length}</span>}
        <span className="small text-muted ms-2">
          Charged by default.{data ? (editable ? ' A shop error comes off the price at approval.' : ' Read-only: the order is not pending.') : ''}
        </span>
        {loading && <span className="spinner-border spinner-border-sm ms-auto" role="status" aria-label="Loading defects"></span>}
      </div>
      <div className="card-body p-2 small">
        {error && (
          <div className="alert alert-warning py-1 mb-2" role="alert">
            Could not read the defects: {error}
            <button type="button" className="btn btn-sm btn-link py-0" onClick={reload}>Try again</button>
          </div>
        )}
        {!data && loading && <div className="text-muted">Loading defects…</div>}
        {data && list.length === 0 && <div className="text-muted">No defects recorded on this order&apos;s jobs.</div>}
        {list.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-1">
              <thead>
                <tr>
                  <th>Defect</th>
                  <th>Job</th>
                  <th>Part</th>
                  <th>Station</th>
                  <th className="text-end">Pieces</th>
                  <th>Cause</th>
                  <th>Recorded</th>
                  <th>Decision</th>
                  <th>Remake links</th>
                  {editable && <th aria-label="Actions"></th>}
                </tr>
              </thead>
              <tbody>
                {list.map((d) => {
                  const dec = defectDecision(d);
                  const pr = d.pricing || {};
                  const sheet = d.defect_type === 'sheet';
                  return (
                    <React.Fragment key={d.defect_id}>
                      <tr data-testid={`tm-defects-row-${d.defect_id}`}>
                        <td>#{d.defect_id}</td>
                        <td>
                          {jobText(d.job_id, jobNums)}
                          {d.job_live === false && <span className="badge bg-dark ms-1">{d.job_status}</span>}
                        </td>
                        <td className="font-monospace">{sheet ? 'ruined sheet' : (d.product_id || (d.part_id != null ? `part ${d.part_id}` : '—'))}</td>
                        <td>{d.station || '—'}</td>
                        <td className="text-end">{d.n}{sheet ? ' sh' : ''}</td>
                        <td>
                          {causeLabel(d.cause_code)}
                          {d.suggest_shop_error && !dec.shopError && (
                            <span className="badge bg-info-subtle text-info-emphasis ms-1" title="A hint for the pricing review, never the decision">suggests shop error</span>
                          )}
                        </td>
                        <td>
                          {d.recorded_by || '?'}{d.created_at ? `, ${stamp(d.created_at)}` : ''}
                          {d.reason && <div className="text-muted text-truncate" style={{ maxWidth: 220 }} title={d.reason}>{d.reason}</div>}
                        </td>
                        <td>
                          <TmDecisionChip decision={dec} />
                          {dec.decided && (
                            <div className="text-muted">
                              {dec.by || '?'}{dec.at ? `, ${stamp(dec.at)}` : ''}{dec.why ? `: ${dec.why}` : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          {(d.links || []).length === 0 && !sheet && <span className="text-muted">none</span>}
                          {(d.links || []).map((lk) => (
                            <div key={lk.link_id}>job {jobText(lk.makeup_job_id, jobNums)} · {lk.qty} pc · {String(lk.source || '').replace(/_/g, ' ')}</div>
                          ))}
                          {!sheet && pr.open_qty > 0 && (d.links || []).length > 0 && <div className="text-muted">{pr.open_qty} pc not linked</div>}
                          {(d.removed_links || []).length > 0 && <div className="text-muted">{d.removed_links.length} removed</div>}
                        </td>
                        {editable && (
                          <td className="text-end">
                            {canDecideDefect(d, data) ? (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                disabled={!!deciding}
                                onClick={() => setDeciding(d)}
                              >
                                Charged or shop error…
                              </button>
                            ) : (
                              <span className="text-muted" title="No line of this order prices this defect, so there is nothing to decide">
                                not priced on this order
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                      {editable && deciding && deciding.defect_id === d.defect_id && (
                        <tr>
                          <td colSpan={10}>
                            <TmDefectDecisionDialog
                              key={`${d.defect_id}-toggle`}
                              salesOrderId={header.sales_order_id}
                              defect={d}
                              mode="toggle"
                              onCancel={() => setDeciding(null)}
                              onDone={() => { setDeciding(null); if (reload) reload(); if (onDecided) onDecided(); }}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {staged.length > 0 && (
          <div className="text-muted">{staged.length} laser defect(s) are staged: they are recorded when their laser task finishes.</div>
        )}
        {orphans > 0 && (
          <div className="text-muted">{orphans} decision(s) or link(s) belong to a defect that was deleted.</div>
        )}
        {items.length > 0 && (
          <ul className="mb-0 ps-3 mt-1">
            {items.map((it, i) => (
              <li key={`${it.code}-${i}`} className={it.severity === 'hard' ? 'text-danger' : 'text-warning-emphasis'}>
                {chipLabel(it.code)}{typeof it.detail === 'string' && it.detail ? ` — ${it.detail}` : ''}
              </li>
            ))}
          </ul>
        )}
        {editable && list.some((d) => d.defect_type !== 'sheet') && (
          <div className="text-muted mt-1">Confirm or remove remake links in Approve T&amp;M Pricing → Scrap.</div>
        )}
      </div>
    </div>
  );
}
